import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { logger } from '@/services/logger';
import { supabase, refreshSession } from '@/integrations/supabase/client';
import { supabaseDirectApi } from '@/utils/supabaseDirectApi';
import { ensureResetPasswordPage } from '@/utils/authHelpers';

interface AppInitializerProps {
  children: React.ReactNode;
}

// Enhanced loading fallback component with status message
const LoadingFallback = ({ message }: { message?: string }) => (
  <div className="flex flex-col h-screen w-screen items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin mb-4" />
    {message && <p className="text-sm text-gray-500">{message}</p>}
  </div>
);

// App initialization component to handle session refresh
const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        logger.info('Initializing application and refreshing session');
        
        // Reset any potential stuck state
        if (localStorage.getItem('is_initializing')) {
          logger.warn('Detected stuck initialization state, clearing');
          localStorage.removeItem('is_initializing');
        }
        
        localStorage.setItem('is_initializing', 'true');
        
        // Mark the app as in initial load state to optimize subscription checks
        localStorage.setItem('app_initial_load', 'true');
        
        // Check for token redirects first before any other initialization
        // This prevents tokens from being lost during the initialization process
        if (typeof window !== 'undefined') {
          // Try multiple approaches to handle auth token redirects
          let wasRedirected = supabaseDirectApi.handleTokenRedirect();
          
          // If the direct API approach didn't redirect, try the auth helpers approach
          if (!wasRedirected) {
            wasRedirected = ensureResetPasswordPage();
          }
          
          if (wasRedirected) {
            logger.info('Auth token detected and redirect initiated. Stopping initialization.');
            return; // Stop initialization since we're redirecting
          }
          
          // Check URL for token parameters that indicate auth flows
          const currentUrl = window.location.href;
          const urlParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
          
          // Check for token-based auth flows
          const token = urlParams.get('token') || hashParams.get('token');
          const type = urlParams.get('type') || hashParams.get('type');
          
          logger.debug('URL analysis', { currentUrl, token: token ? 'present' : 'none', type });
          
          // Check if we're already on the verify page or reset password page
          const isAlreadyOnVerifyPage = currentUrl.includes('/verify');
          const isAlreadyOnResetPage = currentUrl.includes('/reset-password');
          
          if (token && type) {
            logger.info(`Auth token detected with type: ${type}`);
            
            // Log detailed information about the current state
            logger.info('Auth token details', {
              type,
              token: token ? 'present' : 'none',
              isAlreadyOnVerifyPage,
              isAlreadyOnResetPage,
              currentUrl,
              pathname: window.location.pathname
            });
            
            if ((type === 'signup' || type === 'email') && !isAlreadyOnVerifyPage) {
              // Redirect signup/email verification to verify page
              logger.info('Email verification token detected, redirecting to verify page');
              // Preserve both search parameters and hash fragments
              const verifyUrl = `${window.location.origin}/verify${window.location.search}${window.location.hash}`;
              logger.info('Redirecting to verify page', { verifyUrl });
              window.location.href = verifyUrl;
              return; // Stop initialization since we're redirecting
            } else if (type === 'recovery' && !isAlreadyOnResetPage) {
              // Recovery tokens should go to reset-password
              logger.info('Recovery token detected, redirecting to reset password page');
              const resetUrl = `${window.location.origin}/reset-password${window.location.search}${window.location.hash}`;
              logger.info('Redirecting to reset password page', { resetUrl });
              window.location.href = resetUrl;
              return; // Stop initialization since we're redirecting
            }
          }
          
          // Handle PKCE flow for both signup verification and password reset
          const hasCode = urlParams.has('code') || hashParams.has('code');
          if (hasCode) {
            logger.info('PKCE auth code detected, analyzing flow type');
            
            // Check if we're already on the verify or reset-password page
            const isAlreadyOnVerifyPage = currentUrl.includes('/verify');
            const isAlreadyOnResetPage = currentUrl.includes('/reset-password');
            
            // Try to determine if this is a signup verification or password reset
            // Check for session to help determine context
            const { data: sessionData } = await supabase.auth.getSession();
            const hasSession = !!sessionData.session;
            
            // Check for signup-specific indicators
            const isSignupPath = currentUrl.includes('/signup') || 
                                localStorage.getItem('auth_flow') === 'signup';
            
            // Check for login or email confirmation context
            const isEmailConfirmation = localStorage.getItem('email_confirmation_pending') === 'true';
            
            logger.debug('PKCE flow analysis', { 
              hasCode, 
              isAlreadyOnVerifyPage, 
              isAlreadyOnResetPage, 
              isSignupPath, 
              isEmailConfirmation, 
              hasSession 
            });
            
            if ((isEmailConfirmation || isSignupPath || !hasSession) && !isAlreadyOnVerifyPage) {
              // This appears to be email verification - redirect to verify page
              logger.info('PKCE code appears to be for email verification, redirecting to verify page');
              const verifyUrl = `${window.location.origin}/verify${window.location.search}${window.location.hash}`;
              logger.info('Redirecting to verify page', { verifyUrl });
              window.location.href = verifyUrl;
              return; // Stop initialization since we're redirecting
            } else if (!isAlreadyOnResetPage && !isAlreadyOnVerifyPage && localStorage.getItem('auth_flow') === 'recovery') {
              // Only redirect to reset-password if context is strongly for recovery
              logger.info('PKCE code appears to be for password reset (recovery context), redirecting to reset password page');
              
              // Use URL constructor to properly handle search params and hash
              const currentUrl = new URL(window.location.href);
              const resetUrl = new URL(`${window.location.origin}/reset-password`);
              
              // Copy all query parameters
              currentUrl.searchParams.forEach((value, key) => {
                resetUrl.searchParams.append(key, value);
              });
              
              // Copy hash if exists
              if (currentUrl.hash) {
                resetUrl.hash = currentUrl.hash;
              }
              
              logger.info('Redirecting to reset password page', { resetUrl: resetUrl.toString() });
              window.location.href = resetUrl.toString();
              return; // Stop initialization since we're redirecting
            }
          }
          
          // Handle legacy token flow (access_token in hash)
          if (currentUrl.includes('access_token=') && !currentUrl.includes('/reset-password')) {
            // Legacy token flow - direct navigation to reset password page
            logger.info('Legacy token flow detected, redirecting to reset password page');
            const resetUrl = `${window.location.origin}/reset-password${window.location.search}${window.location.hash}`;
            window.location.href = resetUrl;
            return; // Stop initialization since we're redirecting
          }
        }
        
        // Check for existing session first
        const { data } = await supabase.auth.getSession();
        
        if (data.session) {
          logger.info('Found existing session, refreshing token');
          const session = await refreshSession();
          
          if (session) {
            logger.info('Session refreshed successfully');
            
            // Disable immediate subscription verification to prevent race conditions
            // Store a flag to indicate we should skip immediate verification
            localStorage.setItem('skip_immediate_verification', 'true');
            
            // Set a timeout to remove this flag after the app has fully initialized
            setTimeout(() => {
              localStorage.removeItem('skip_immediate_verification');
            }, 5000); // 5 seconds should be enough for providers to initialize
          } else {
            logger.warn('Session refresh failed, but continuing');
          }
        } else {
          logger.info('No existing session found');
        }
        
        // Small delay to ensure everything is properly initialized
        await new Promise(resolve => setTimeout(resolve, 1000));
        
      } catch (error) {
        logger.error('Error during initialization', { error });
        setInitError('Failed to initialize application');
      } finally {
        localStorage.removeItem('is_initializing');
        setIsInitializing(false);
      }
    };

    initializeApp();
  }, []);

  if (isInitializing) {
    return <LoadingFallback message="Initializing application..." />;
  }

  if (initError) {
    return (
      <div className="flex flex-col h-screen w-screen items-center justify-center">
        <p className="text-red-500 mb-2">{initError}</p>
        <button 
          className="px-4 py-2" 
          style={{ background: 'var(--color-primary)', color: '#fff', borderRadius: '8px', fontWeight: 600 }}
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default AppInitializer; 